/**
 * Browser side of passkeys (identity contract, flag identity.passkeys). The API answers 404 on every passkey
 * route while the flag is off, so the UI probes the endpoints and shows nothing passkey-related on 404.
 * Ceremonies use @simplewebauthn/browser (JSON <-> WebAuthn conversions, conditional mediation, abort handling).
 */
import {
  browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, startAuthentication, startRegistration,
  type PublicKeyCredentialCreationOptionsJSON, type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { PasskeyRegistered, PasskeyView } from '@qm/contracts/identity';
import { BffError, bffJson } from './bff';

export type SignInResult = { next: string };

export const passkeysSupported = (): boolean => typeof window !== 'undefined' && browserSupportsWebAuthn();
export const autofillSupported = (): Promise<boolean> => (passkeysSupported() ? browserSupportsWebAuthnAutofill() : Promise.resolve(false));

/** Sign-in challenge; null when passkeys are off (404) or unavailable, so the caller hides the option. */
export async function signInOptions(): Promise<PublicKeyCredentialRequestOptionsJSON | null> {
  if (!passkeysSupported()) return null;
  try {
    return await bffJson<PublicKeyCredentialRequestOptionsJSON>('/v1/identity/passkeys/signin/options', 'POST', {});
  } catch {
    return null;
  }
}

/**
 * Passwordless sign-in. With `autofill`, the ceremony waits on the browser's autofill (conditional mediation)
 * and resolves only when the person picks a passkey from the email field's suggestions.
 */
export async function passkeySignIn(opts: { options?: PublicKeyCredentialRequestOptionsJSON; autofill?: boolean } = {}): Promise<SignInResult> {
  const optionsJSON = opts.options ?? (await bffJson<PublicKeyCredentialRequestOptionsJSON>('/v1/identity/passkeys/signin/options', 'POST', {}));
  const response = await startAuthentication({ optionsJSON, useBrowserAutofill: opts.autofill === true });
  return bffJson<SignInResult>('/v1/identity/passkeys/signin/verify', 'POST', { response });
}

/** Second factor for a session awaiting 2FA. */
export async function passkeySecondFactor(): Promise<SignInResult> {
  const optionsJSON = await bffJson<PublicKeyCredentialRequestOptionsJSON>('/v1/identity/passkeys/2fa/options', 'POST', {});
  const response = await startAuthentication({ optionsJSON });
  return bffJson<SignInResult>('/v1/identity/passkeys/2fa/verify', 'POST', { response });
}

/**
 * Register a passkey for the signed-in person (also completes a required 2FA enrolment). recoveryCodes comes back
 * once when the person held none (passkey-only accounts); the caller must show them.
 */
export async function registerPasskey(name?: string): Promise<PasskeyRegistered> {
  const optionsJSON = await bffJson<PublicKeyCredentialCreationOptionsJSON>('/v1/identity/passkeys/registration/options', 'POST', {});
  const response = await startRegistration({ optionsJSON });
  return bffJson<PasskeyRegistered>('/v1/identity/passkeys/registration/verify', 'POST', { response, ...(name ? { name } : {}) });
}

export const renamePasskey = (id: string, name: string) => bffJson<PasskeyView>(`/v1/identity/passkeys/${encodeURIComponent(id)}`, 'PATCH', { name });
export const removePasskey = (id: string) => bffJson<{ removed: boolean }>(`/v1/identity/passkeys/${encodeURIComponent(id)}`, 'DELETE');

/** Is this "passkeys are switched off" (the flag answers 404)? */
export const isPasskeysOff = (err: unknown): boolean => err instanceof BffError && err.status === 404;

/** i18n key for a failed ceremony or API refusal. */
export function passkeyErrorKey(err: unknown): string {
  const name = (err as { name?: string; cause?: { name?: string } } | null)?.name;
  const cause = (err as { cause?: { name?: string } } | null)?.cause?.name;
  if (name === 'NotAllowedError' || name === 'AbortError' || cause === 'NotAllowedError' || cause === 'AbortError'
    || (err as { code?: string } | null)?.code === 'ERROR_CEREMONY_ABORTED') return 'shell.passkeys.error.cancelled';
  if (err instanceof BffError) {
    const reason = (err.envelope?.error as { details?: { reason?: string } } | undefined)?.details?.reason;
    if (reason === 'last_factor') return 'shell.passkeys.error.lastFactor';
    if (reason === 'passkey_limit') return 'shell.passkeys.error.limit';
    if (err.status === 429) return 'shell.passkeys.error.rateLimited';
  }
  return 'shell.passkeys.error.failed';
}
